<template lang="pug">
  .b-supplier-layout
    header._header
      ._logo TeRoom
      nav._menu
        router-link._item(
          v-for="link in links"
          :key="link.label"
          :to="link.to"
          :data-testid="link.testId"
          ) {{ link.label }}
        //- Возврат из режима «войти под пользователем» (ТЗ 12.3, раздел об администрировании):
        //- в кабинете нет шапки приложения с меню аккаунта, поэтому выход из режима нужен здесь —
        //- иначе администратор, переключившийся на поставщика, не может вернуться к себе
        button._item._impersonator(
          v-if="isImpersonation"
          type="button"
          data-testid="supplier-menu-exit-impersonation"
          @click="exitImpersonation"
          ) Опять стать админом
        button._item(type="button" data-testid="supplier-menu-logout" @click="logout") Выход
    main._main
      ErrorPage(v-if="error" :code="error.status")
      slot(v-else)
</template>

<script>
import auth from '@/model/auth';
import user from '@/model/user';
import ErrorPage from '@/components/ErrorPage.vue';

/**
 * Оболочка кабинета поставщика (ТЗ 6.1): меню только «Запросы», «Чаты», «Уведомления», «Профиль»
 * и выход — остальной интерфейс TeRoom поставщику не показывается, поэтому общая оболочка с левым
 * и верхним меню приложения здесь не используется.
 *
 * Своя оболочка, а не настройка общей: у общей в шапке меню аккаунта со ссылками на разделы,
 * закрытые для поставщика. Сгенерировано Claude.
 */
export default {
  name: 'SupplierLayout',
  components: { ErrorPage },
  data() {
    return {
      links: [
        { label: 'Запросы', to: { name: 'supplier-requests' }, testId: 'supplier-menu-requests' },
        { label: 'Чаты', to: { name: 'supplier-chats' }, testId: 'supplier-menu-chats' },
        { label: 'Уведомления', to: { name: 'notifications' }, testId: 'supplier-menu-notifications' },
        { label: 'Профиль', to: { name: 'supplier-profile' }, testId: 'supplier-menu-profile' },
      ],
    };
  },
  computed: {
    error() { return this.$store.getters['error/err403'] || this.$store.getters['error/err404']; },
    isImpersonation() { return !!this.$store.state.auth.user?.isImpersonation; },
  },
  methods: {
    /**
     * Возврат в свою учётную запись: после переключения текущий пользователь перечитывается, и
     * кабинет уступает место обычному интерфейсу — перенаправление делает глобальный `beforeEach`.
     */
    exitImpersonation() {
      user.impersonate().then(() => {
        this.$router.push({ name: 'home' }).catch(() => {});
      });
    },
    logout() {
      auth.logout().then(() => {
        auth.goToRouteAfterLogin(this.$route.fullPath);
        this.$router.push({ name: 'auth-login' });
      });
    },
  },
};
</script>

<style scoped lang="scss">
.supplier-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #F5F7FB;

  &__header {
    display: flex;
    align-items: center;
    gap: 40px;
    padding: 16px 34px;
    background: linear-gradient(180deg, #2393FF 0%, #35A3FB 100%);
    color: var(--color-white);
  }

  &__logo {
    font-weight: 800;
    font-size: 20px;
  }

  &__menu {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  &__item {
    border: none;
    background: none;
    padding: 0;
    font: inherit;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;

    &.router-link-active {
      color: var(--color-white);
      text-decoration: underline;
    }
  }

  &__impersonator {
    color: #FFE07A;
  }

  &__main {
    flex-grow: 1;
    box-sizing: border-box;
    padding: 30px 34px 60px;
  }

  // Узкие экраны (ТЗ 6.6, 6.10 — общий порог модуля 768 px): шапка кабинета переносится в две
  // строки, меню пунктов не сжимается в нечитаемое, отступы содержимого уменьшаются
  @media (max-width: 768px) {
    &__header {
      flex-wrap: wrap;
      gap: 10px 20px;
      padding: 12px 14px;
    }

    &__menu {
      flex-wrap: wrap;
      gap: 12px 16px;
    }

    &__main {
      padding: 16px 14px 40px;
    }
  }
}
</style>
